// core/stores/followStore.ts — Zustand store for follow state.
//
// Keyed by normalised handle (no @ prefix, lowercased) → boolean.
// Hydrated at module init from the typed seed (seedFollows).
// Services mutate this store; hooks read it reactively.
//
// Legacy follow-state.js used a plain Set and normed handles the same way:
//   norm(handle) = handle.replace(/^@/, '').toLowerCase()
// We preserve that normalisation contract here.

import { create } from 'zustand';
import type { FollowState } from '@/core/models/follow';
import { seedFollows } from '@/core/data/follows';
import { bootSeed } from './bootSeed';

/** Normalise a handle for keying: strip leading @, lowercase. */
export function normHandle(handle: string): string {
  return handle.replace(/^@/, '').toLowerCase();
}

interface FollowStoreState {
  follows: FollowState; // { [normHandle]: true }
  // Pending OUTGOING follow requests to PRIVATE accounts — { [normHandle]: true }.
  // Hydrated at boot from the outgoing-requests list, same place muteStore's
  // hydration happens (see hooks/useGlobalHydration.ts). Distinct from `follows`:
  // a handle can be `requested` without ever being `following`.
  requested: FollowState;

  // Set/clear a handle in the follow set.
  setFollowing: (handle: string, following: boolean) => void;

  // Set/clear a handle in the pending-request set.
  setRequested: (handle: string, requested: boolean) => void;

  // Replace the entire follow map (used by _resetForTest).
  setFollows: (follows: FollowState) => void;

  // Replace the entire pending-request map (boot hydration / _resetForTest).
  setRequests: (requested: FollowState) => void;
}

/** Build the initial follow map from the seed list. */
function buildInitialFollows(): FollowState {
  const map: FollowState = {};
  for (const entry of bootSeed(seedFollows)) {
    map[normHandle(entry.handle)] = true;
  }
  return map;
}

export const useFollowStore = create<FollowStoreState>()((set) => ({
  follows: buildInitialFollows(),
  requested: {},

  setFollowing: (handle, following) =>
    set((s) => {
      const key = normHandle(handle);
      if (following) {
        return { follows: { ...s.follows, [key]: true } };
      }
      const next = { ...s.follows };
      delete next[key];
      return { follows: next };
    }),

  setRequested: (handle, requested) =>
    set((s) => {
      const key = normHandle(handle);
      if (requested) {
        return { requested: { ...s.requested, [key]: true } };
      }
      const next = { ...s.requested };
      delete next[key];
      return { requested: next };
    }),

  setFollows: (follows) => set({ follows }),

  setRequests: (requested) => set({ requested }),
}));
